import { useCallback, useEffect, useId, useRef } from 'react';
import './Modal.css';

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * Оболочка модального окна: фокус внутри, Escape и щелчок по фону закрывают.
 *
 * Одна на все окна панели намеренно. Пока их было одно, поведение фокуса жило
 * прямо в разметке формы; со вторым окном это означало бы два окна с разными
 * правилами в одной форме — оператор бы гадал, что делает Escape в каждом из
 * них. Закрытие ведёт к безопасному исходу: случайное нажатие отменяет
 * операцию, а не подтверждает её.
 */
const Modal = ({ title, onClose, className = '', children }) => {
  const dialogRef = useRef(null);
  const titleId = useId();

  // Фокус возвращается туда, откуда окно открыли: иначе после закрытия он
  // оказывается на <body>, и клавиатурный оператор начинает обход страницы
  // сначала — вместо кнопки, которую только что нажал.
  useEffect(() => {
    const openedFrom = document.activeElement;
    dialogRef.current?.focus();

    return () => openedFrom?.focus?.();
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      // Без этого Tab уводит фокус на кнопки формы под окном — те самые, что
      // окно и должно перекрывать.
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(FOCUSABLE) ?? [],
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (
        event.shiftKey &&
        (active === first || active === dialogRef.current)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  // mouseDown, а не click: иначе выделение текста в окне, отпущенное над
  // фоном, закрывает окно — а текст здесь выделяют, чтобы сверить адрес.
  const handleOverlayMouseDown = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className="modal_overlay"
      role="presentation"
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        ref={dialogRef}
        className={`modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <h4 id={titleId}>{title}</h4>
        {children}
      </div>
    </div>
  );
};

export default Modal;
